/* One native textarea owns the illustrative guide preview. Exact review stays on the product. */
(() => {
  'use strict';
  if (window.tfInscriptionPreviewBound) return;
  window.tfInscriptionPreviewBound = true;
  const states = new WeakMap();
  const languageKey = 'tf:guide-language-draft:v1';
  const lifetime = 120000;
  let languageArmed = null;
  const locale = value => {
    const code = typeof value === 'string' ? value.toLowerCase().split('-')[0] : '';
    return ['de', 'fr', 'it'].includes(code) ? code : null;
  };
  const clearLanguage = () => {
    try { window.sessionStorage.removeItem(languageKey); return true; }
    catch { return false; }
  };
  const restoreLanguage = (root, input) => {
    let record;
    try {
      const raw = window.sessionStorage.getItem(languageKey);
      if (raw === null) return;
      window.sessionStorage.removeItem(languageKey);
      if (window.sessionStorage.getItem(languageKey) !== null) return;
      record = JSON.parse(raw);
    } catch { return; }
    const current = window.tfGuideDraft?.describe(root, false);
    const age = Date.now() - record?.captured_at;
    if (!current || !record || record.schema !== 1 || record.source !== 'guide-language' || !Number.isSafeInteger(record.captured_at) || age < 0 || age > lifetime || record.origin !== window.location.origin || record.to_locale !== current.locale || locale(record.from_locale) !== record.from_locale || record.from_locale === record.to_locale || record.product_id !== current.product_id || record.variant_id !== current.variant_id || record.pattern !== 'classic' || record.capped !== 'false' || typeof record.text !== 'string') return;
    try {
      const from = new URL(document.referrer);
      const suffix = '/pages/gravur';
      if (from.origin !== record.origin || from.pathname !== record.from_path || !from.pathname.endsWith(suffix)) return;
      const sourceBase = from.pathname.slice(0, -suffix.length);
      if ((sourceBase !== '' && !/^\/(de|fr|it)(-[a-z]{2})?$/u.test(sourceBase)) || (sourceBase && locale(sourceBase.slice(1)) !== record.from_locale) || (!sourceBase && record.from_locale !== 'de')) return;
      const before = input.value;
      input.value = record.text;
      if (input.value !== record.text) { input.value = before; throw new Error('Guide text changed'); }
    } catch {
      return root.dataset.tfLanguageError || root.dataset.tfTransferError;
    }
  };
  const setMessage = (region, message) => {
    const child = region?.querySelector('[data-tf-status-message]');
    if (!child) return;
    child.textContent = message || '';
    region.hidden = !message;
  };
  const paint = (node, raw) => {
    if (node.namespaceURI === 'http://www.w3.org/2000/svg' && node.tagName.toLowerCase() === 'text') {
      const x = node.getAttribute('x') || '0';
      const y = Number(node.getAttribute('y')) || 0;
      const step = Number(node.dataset.tfPreviewLineStep) || 22.5;
      const offsets = (node.dataset.tfPreviewLineOffsets || '').split(',').map(Number);
      const lines = raw.split('\n').map((line, index) => {
        const span = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        span.setAttribute('x', String(Number.isFinite(offsets[index]) && offsets[index] > 0 ? offsets[index] : x));
        span.setAttribute('y', String(y + index * step));
        span.setAttribute('xml:space', 'preserve');
        // An empty explicit line still owns a baseline; raw text remains only in the textarea.
        span.textContent = line;
        return span;
      });
      node.replaceChildren(...lines);
    } else node.textContent = raw;
  };
  const copy = async (root, input) => {
    const value = input.value;
    let copied = false;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(value);
      copied = true;
    } catch {
      // Useful explicit recovery on older browsers or a denied Clipboard permission.
      input.focus({ preventScroll: true });
      input.select();
      try { copied = typeof document.execCommand === 'function' && document.execCommand('copy'); }
      catch { copied = false; }
    }
    if (!root.isConnected) return;
    if (copied && input.value === value) {
      setMessage(root.querySelector('[data-tf-preview-error]'), '');
      setMessage(root.querySelector('[data-tf-preview-status]'), root.dataset.tfCopySuccess);
    } else {
      setMessage(root.querySelector('[data-tf-preview-status]'), '');
      setMessage(root.querySelector('[data-tf-preview-error]'), root.dataset.tfCopyError);
    }
  };
  const initialise = scope => {
    for (const root of scope.querySelectorAll('[data-tf-inscription-preview]')) {
      if (states.has(root)) continue;
      const fields = root.querySelectorAll('[data-tf-preview-input]');
      const input = fields[0];
      const texts = Array.from(root.querySelectorAll('[data-tf-preview-text],[data-tf-preview-text-inverse]'));
      const link = root.querySelector('[data-tf-preview-continue]');
      const error = root.querySelector('[data-tf-preview-error]');
      if (fields.length !== 1 || input.tagName !== 'TEXTAREA' || texts.length !== 2 || !link || !error?.querySelector('[data-tf-status-message]')) continue;
      const rules = window.tfInscriptionRules;
      const messageFor = result => result.tooMany ? root.dataset.tfLineError : result.tooLong ? (root.dataset.tfWidthError || '').replace('{line}', String(result.tooLong)) : '';
      const update = () => {
        const result = rules?.inspect(input.value);
        const message = result ? messageFor(result) : '';
        // An invalid value stays intact in the field, but never spills across a
        // slit or past the photographic metal in the illustrative view.
        texts.forEach(text => paint(text, message ? '' : input.value));
        window.tfGuideDraft?.clear();
        clearLanguage();
        input.setCustomValidity('');
        if (message) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
        setMessage(error, message);
        setMessage(root.querySelector('[data-tf-preview-status]'), '');
        const counter = root.querySelector('[data-tf-line-count]');
        if (counter) counter.textContent = (root.dataset.tfCountLabel || '{count}/7').replace('{count}', String(input.value ? result?.lineCount || 1 : 0));
        link.setAttribute('aria-disabled', /\S/u.test(input.value) && !message ? 'false' : 'true');
      };
      const onContinue = event => {
        if (event.defaultPrevented) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) { window.tfGuideDraft?.clear(); return; }
        if (!/\S/u.test(input.value)) {
          event.preventDefault();
          input.setCustomValidity(root.dataset.tfRequiredText || root.dataset.tfTransferError);
          input.reportValidity();
          return;
        }
        const message = rules ? messageFor(rules.inspect(input.value)) : '';
        if (message) {
          event.preventDefault();
          input.setCustomValidity(message);
          input.reportValidity();
          return;
        }
        if (!window.tfGuideDraft?.save(root, input.value)) {
          event.preventDefault();
          setMessage(error, root.dataset.tfTransferError);
          const recovery = root.querySelector('[data-tf-preview-recovery]');
          if (recovery) recovery.hidden = false;
          error.tabIndex = -1;
          error.focus({ preventScroll: true });
        } else {
          queueMicrotask(() => { if (event.defaultPrevented) window.tfGuideDraft?.clear(); });
        }
        // A successful ordinary native link navigation carries only the short-lived session record.
      };
      const onSurface = event => {
        if (event.target.closest('a,button,input,textarea,select')) return;
        input.focus({ preventScroll: true });
      };
      const onCopy = () => copy(root, input);
      const onManual = () => window.tfGuideDraft?.clear();
      const surface = root.querySelector('[data-tf-preview-surface]');
      const button = root.querySelector('[data-tf-preview-copy]');
      const unbindInput = rules?.bindInput(input, () => setMessage(error, root.dataset.tfLineError));
      input.addEventListener('input', update);
      link.addEventListener('click', onContinue);
      link.addEventListener('auxclick', onManual);
      link.addEventListener('contextmenu', onManual);
      surface?.addEventListener('click', onSurface);
      button?.addEventListener('click', onCopy);
      states.set(root, () => {
        unbindInput?.();
        input.removeEventListener('input', update);
        link.removeEventListener('click', onContinue);
        link.removeEventListener('auxclick', onManual);
        link.removeEventListener('contextmenu', onManual);
        surface?.removeEventListener('click', onSurface);
        button?.removeEventListener('click', onCopy);
        states.delete(root);
      });
      root.dataset.tfPreviewReady = 'true';
      const restoreError = restoreLanguage(root, input);
      update();
      document.fonts?.ready?.then(() => { if (root.isConnected) update(); });
      if (restoreError) setMessage(error, restoreError);
    }
  };
  const initialiseRecovery = scope => {
    for (const root of scope.querySelectorAll('[data-tf-guide-recovery]')) {
      if (states.has(root)) continue;
      const input = root.querySelector('[data-tf-guide-recovery-input]');
      const button = root.querySelector('[data-tf-preview-copy]');
      if (!input || !button) continue;
      const onCopy = () => copy(root, input);
      button.addEventListener('click', onCopy);
      states.set(root, () => { button.removeEventListener('click', onCopy); states.delete(root); });
    }
  };
  const start = scope => { initialise(scope); initialiseRecovery(scope); };
  // Capture the explicit native language intent before the existing submit-on-change owner.
  document.addEventListener('change', event => {
    const form = document.getElementById('TfHeaderLanguage');
    const language = form?.querySelector('[data-tf-language]');
    if (event.target !== language) return;
    languageArmed = null;
    const roots = document.querySelectorAll('[data-tf-inscription-preview]');
    if (roots.length !== 1 || !states.has(roots[0])) return;
    const root = roots[0];
    const current = window.tfGuideDraft?.describe(root, false);
    const target = locale(language.value);
    if (!current || !target || target === current.locale || !Array.from(language.options).some(option => option.value === language.value)) { clearLanguage(); return; }
    const intent = { root, current, target, form, previous: Array.from(language.options).find(option => locale(option.value) === current.locale)?.value };
    languageArmed = intent;
    queueMicrotask(() => { if (languageArmed === intent) languageArmed = null; });
  }, true);
  document.addEventListener('submit', event => {
    const intent = languageArmed;
    if (!intent || event.target !== intent.form) return;
    languageArmed = null;
    if (event.defaultPrevented || !intent.root.isConnected || !states.has(intent.root)) return;
    const language = intent.form.querySelector('[data-tf-language]');
    if (locale(language?.value) !== intent.target) return;
    const input = intent.root.querySelector('[data-tf-preview-input]');
    const value = input.value;
    let serialized;
    if (value === '') { clearLanguage(); return; }
    try {
      if (!clearLanguage()) throw new Error('Language draft unavailable');
      const record = { schema: 1, source: 'guide-language', captured_at: Date.now(), origin: window.location.origin, from_locale: intent.current.locale, to_locale: intent.target, from_path: intent.current.source_path, product_id: intent.current.product_id, variant_id: intent.current.variant_id, pattern: 'classic', capped: 'false', text: value };
      serialized = JSON.stringify(record);
      window.sessionStorage.setItem(languageKey, serialized);
      if (window.sessionStorage.getItem(languageKey) !== serialized) throw new Error('Language draft not retained');
    } catch {
      event.preventDefault();
      clearLanguage();
      if (intent.previous) language.value = intent.previous;
      setMessage(intent.root.querySelector('[data-tf-preview-error]'), intent.root.dataset.tfLanguageError || intent.root.dataset.tfTransferError);
      return;
    }
    queueMicrotask(() => {
      if (!event.defaultPrevented) return;
      try { if (window.sessionStorage.getItem(languageKey) === serialized) window.sessionStorage.removeItem(languageKey); } catch {}
    });
  }, true);
  start(document);
  document.addEventListener('shopify:section:load', event => start(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-tf-inscription-preview],[data-tf-guide-recovery]').forEach(root => states.get(root)?.());
  });
})();
